/**
 * Side-by-side lanes for calendar blocks that overlap in time, such as two activities logged over
 * the same hour. Pure presentation geometry for the day and week grids: it decides only where a
 * block is drawn, never what is scheduled or recorded.
 *
 * Blocks form clusters, the maximal groups connected by overlap. Inside a cluster each block takes
 * the lowest-numbered lane whose previous block has already ended (greedy interval-graph colouring
 * in start order, which uses the fewest lanes possible). Every block in a cluster then draws at
 * width `1 / lanes`, offset by `lane / lanes`. Intervals are half-open, so back-to-back blocks do
 * not share a cluster and keep the full width.
 *
 * @module apps/web/src/components/calendar/timelineLanes
 */

/** One block to place: identity plus its start and end in epoch milliseconds. */
export interface LaneInterval {
  /** Rendering identity of the block; must be unique within one call. */
  id: string;
  /** Start in epoch milliseconds. */
  start: number;
  /** End in epoch milliseconds; a non-positive length is treated as one minute so it still occupies a lane. */
  end: number;
}

/** Where one block is drawn inside its day column. */
export interface LanePlacement {
  /** Zero-based lane, counted from the left. */
  lane: number;
  /** Lanes in the block's overlap cluster; 1 means the block has the column to itself. */
  lanes: number;
}

/** Shortest length a block occupies for layout, so a zero-length placement still gets a lane. */
const MIN_LAYOUT_MS = 60_000;

/** Assign each interval a lane and its cluster's lane count. Runs in O(n log n + n·k) for k lanes. */
export function assignLanes(intervals: LaneInterval[]): Map<string, LanePlacement> {
  const sorted = intervals
    .filter(item => Number.isFinite(item.start) && Number.isFinite(item.end))
    .map(item => ({ ...item, end: Math.max(item.end, item.start + MIN_LAYOUT_MS) }))
    .sort((left, right) => left.start - right.start || right.end - left.end);
  const placements = new Map<string, LanePlacement>();
  let cluster: string[] = [];
  let laneEnds: number[] = [];
  let clusterEnd = -Infinity;
  const closeCluster = () => {
    for (const id of cluster) placements.get(id)!.lanes = laneEnds.length;
    cluster = [];
    laneEnds = [];
  };
  for (const item of sorted) {
    if (item.start >= clusterEnd) closeCluster();
    let lane = laneEnds.findIndex(end => end <= item.start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(item.end);
    } else laneEnds[lane] = item.end;
    placements.set(item.id, { lane, lanes: 1 });
    cluster.push(item.id);
    clusterEnd = Math.max(clusterEnd, item.end);
  }
  closeCluster();
  return placements;
}

/** Read a calendar block's identity and ISO start/end as a lane interval; unparseable times are skipped by assignLanes. */
export function laneInterval(block: {
  /** Rendering identity. */
  id: string;
  /** ISO start time. */
  startTime: string;
  /** ISO end time. */
  endTime: string;
}): LaneInterval {
  return { id: block.id, start: Date.parse(block.startTime), end: Date.parse(block.endTime) };
}

/** Inline CSS that places a block in its lane; it overrides the block's full-width classes. */
export interface LaneStyle {
  /** Left offset as a percentage of the day column. */
  left: string;
  /** Width: the lane's share of the column minus a 2 px gutter. */
  width: string;
  /** Always `auto`, so the block's `right-0` class does not stretch it back to full width. */
  right: string;
}

/** Horizontal CSS placement for a lane: percentage left offset and width, with a 2 px gutter between lanes. */
export function laneStyle(lane = 0, lanes = 1): LaneStyle | null {
  if (lanes <= 1) return null;
  const share = 100 / lanes;
  return {
    left: `${lane * share}%`,
    width: `calc(${share}% - 2px)`,
    right: 'auto',
  };
}
